// Admin config-as-code extension point: the customer's initial control-plane
// configuration, applied by `admin apply` over the existing admin REST API.
//
// Every entity is referenced by its NATURAL KEY (a segment by name, a resource by name),
// never by UUID: the config is written by humans and must stay stable across
// environments. The tool resolves names to ids from the list endpoints.
//
// Per ADR-0005 the database is the operational source of truth and this config is the
// initial seed — editing a value later in the admin UI is NOT drift.
import { z } from 'zod';
import { EnvVarNameSchema } from './env-var-name.js';
import { McpDescriptorSchema } from './mcp-descriptor.js';
import { SkillManifestSchema } from './skill-manifest.js';
export const SegmentConfigSchema = z.strictObject({
    name: z.string().min(1),
    description: z.string().min(1).optional(),
    /** Exactly one segment should be the default; `validate` checks that separately. */
    isDefault: z.boolean().default(false),
});
/** Mirror of the server's CreateProviderSchema type enum (providers.controller.ts). */
export const ProviderTypeSchema = z.enum(['OPENAI', 'AZURE_OPENAI', 'ANTHROPIC', 'BEDROCK', 'ECHOPROXY']);
// `apiKeyEnvVar`: the env-var NAME holding the API key — resolved by `admin apply` from the
// operator's environment at CREATE time; the value never appears in the config, the bundle or
// the export. Must also be declared in the `secrets` inventory (`validate` checks that).
/**
 * LLM provider (config-as-code seed, EW-135).
 *
 * Deliberately NO Zod defaults on the optional fields: a declared field is a managed
 * field. An omitted field takes the server's own default on CREATE and is never diffed on
 * UPDATE, so a later admin-UI edit of it survives a re-run of `admin apply` (ADR-0005).
 * `isActive` is absent on purpose: deactivation is an admin operation with a
 * binding-deleting cascade, not a seed concern.
 */
export const ProviderConfigSchema = z
    .strictObject({
    name: z.string().min(1),
    type: ProviderTypeSchema,
    baseUrl: z.url().optional(),
    apiKeyEnvVar: EnvVarNameSchema.optional(),
    /** Azure-specific API version (e.g. `2024-12-01-preview`). */
    apiVersion: z.string().min(1).optional(),
    /** AWS region for Bedrock. */
    region: z.string().min(1).optional(),
})
    .refine((value) => (value.type !== 'ECHOPROXY') === (value.apiKeyEnvVar !== undefined), {
    message: 'apiKeyEnvVar is required for every provider type except ECHOPROXY, and must be omitted for ECHOPROXY (EchoProxy credentials live on each model)',
    path: ['apiKeyEnvVar'],
});
/**
 * Model under a declared provider (config-as-code seed, EW-135). Same "declared field =
 * managed field" contract as ProviderConfigSchema: no Zod defaults on optional fields.
 * `baseUrl` + `apiKeyEnvVar` are only legal on models of an ECHOPROXY provider — that
 * needs the provider list, so `validate` checks it as a cross-reference rule.
 */
export const ModelConfigSchema = z.strictObject({
    /** Owning provider, by DECLARED config provider name. */
    provider: z.string().min(1),
    name: z.string().min(1),
    deploymentId: z.string().min(1).optional(),
    baseUrl: z.url().optional(),
    apiKeyEnvVar: EnvVarNameSchema.optional(),
    contextWindowTokens: z.number().int().positive(),
    maxOutputTokens: z.number().int().positive().optional(),
    inputTokenPrice: z.number().nonnegative(),
    outputTokenPrice: z.number().nonnegative(),
    cachedInputTokenPrice: z.number().nonnegative().optional(),
    cacheWriteTokenPrice: z.number().nonnegative().optional(),
    reasoningTokenPrice: z.number().nonnegative().optional(),
    supportsReasoning: z.boolean().optional(),
    inputModalities: z.array(z.enum(['text', 'image', 'pdf', 'audio', 'video'])).optional(),
});
export const GroupMappingConfigSchema = z.strictObject({
    /** Entra group object id. */
    idpGroupId: z.string().min(1),
    /** Human-readable group name, shown in the admin UI. */
    idpGroupDisplayName: z.string().min(1),
    /** Target segment, by name. */
    segment: z.string().min(1),
});
/**
 * Cost limits. Accepts ORG and SEGMENT scope only — USER is deliberately absent from
 * the enum, so a USER value fails validation with the allowed values named. Reason: there
 * is no name→UUID lookup for users, and users are created by JIT sync, so at seed time
 * they typically do not exist yet.
 */
export const CostLimitScopeSchema = z.enum(['ORG', 'SEGMENT']);
export const CostLimitConfigSchema = z
    .strictObject({
    scopeType: CostLimitScopeSchema,
    /** Segment name — required for SEGMENT scope, must be absent for ORG. */
    segment: z.string().min(1).optional(),
    monthlyBudgetUsd: z.number().positive(),
})
    .refine((value) => (value.scopeType === 'SEGMENT') === (value.segment !== undefined), {
    message: 'segment is required for SEGMENT scope and must be omitted for ORG scope',
    path: ['segment'],
});
// No PROVIDER: the type retired with EW-192 — a provider binding granted nothing, and the
// control plane no longer accepts it.
export const BindingResourceTypeSchema = z.enum(['MODEL', 'SKILL', 'MCP', 'KNOWLEDGE_BASE']);
export const BindingConfigSchema = z.strictObject({
    /** Segment name the resource is bound to. */
    segment: z.string().min(1),
    resourceType: BindingResourceTypeSchema,
    /** Resource name — resolved to an id from the matching list endpoint. */
    resource: z.string().min(1),
});
export const AdminConfigSchema = z.strictObject({
    segments: z.array(SegmentConfigSchema).default([]),
    providers: z.array(ProviderConfigSchema).default([]),
    models: z.array(ModelConfigSchema).default([]),
    groupMappings: z.array(GroupMappingConfigSchema).default([]),
    costLimits: z.array(CostLimitConfigSchema).default([]),
    /** Custom MCP connectors to register (see also the services[] declaration). */
    mcps: z.array(McpDescriptorSchema).default([]),
    skills: z.array(SkillManifestSchema).default([]),
    bindings: z.array(BindingConfigSchema).default([]),
});
//# sourceMappingURL=admin-config.js.map